// FunctionsLoadTemplate.jsx
// Configuration - Update these if your repository structure changes
const GITHUB_CONFIG = {
    owner: 'jamiekayam',
    repo: 'OneWriter-Templates-Public',
    branch: 'main', // or 'master'
    templatesPath: '', // empty string = root directory
};

export const loadTemplateFromGitHub = async ({ setRows, setColumns, setListItems, onReceiveSavedStyles }) => {
    try {
        console.log('Accessing public repository');

        // Construct API URL from config
        // If templatesPath is empty, just get the root contents
        const path = GITHUB_CONFIG.templatesPath ? `/${GITHUB_CONFIG.templatesPath}` : '';
        const apiUrl = `https://api.github.com/repos/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/contents${path}`;

        const headers = {
            'Accept': 'application/vnd.github.v3+json'
        };

        console.log('Fetching from GitHub API:', apiUrl);
        const response = await fetch(apiUrl, { headers });

        // Check for rate limiting
        if (response.status === 403) {
            const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining');
            const rateLimitReset = response.headers.get('X-RateLimit-Reset');

            if (rateLimitRemaining === '0') {
                const resetTime = new Date(parseInt(rateLimitReset) * 1000);
                console.error('GitHub API rate limit exceeded. Resets at:', resetTime);
                alert(`GitHub API rate limit exceeded. Please try again after ${resetTime.toLocaleTimeString()}`);
                return;
            }
        }

        if (!response.ok) {
            console.error('GitHub API fetch failed:', response.status, response.statusText);
            const errorBody = await response.text();
            console.error('Error body:', errorBody);

            // More helpful error messages
            if (response.status === 404) {
                alert(`Repository not found!\n\nPlease verify:\n1. Repository exists: https://github.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}\n2. Repository is public`);
            } else if (response.status === 403) {
                alert('Access forbidden. This might be a rate limit or permissions issue.');
            } else {
                alert(`GitHub API error (${response.status}): ${response.statusText}`);
            }
            throw new Error(`GitHub API error: ${response.status}`);
        }

        const files = await response.json();
        console.log('Files fetched successfully:', files.length);

        // Log rate limit info for debugging
        const rateLimitRemaining = response.headers.get('X-RateLimit-Remaining');
        console.log('GitHub API calls remaining:', rateLimitRemaining);

        if (files.length > 0) {
            console.log('First file structure:', files[0]);
        }

        // Filter for .onewriter and .json files
        const templateFiles = files.filter(file =>
            file.name.toLowerCase().endsWith('.onewriter') ||
            file.name.toLowerCase().endsWith('.json')
        );

        if (templateFiles.length === 0) {
            alert('No template files found in the repository.');
            return;
        }

        // Create and display modal for file selection
        showTemplateSelectionModal(templateFiles, { setRows, setColumns, setListItems, onReceiveSavedStyles });

    } catch (error) {
        console.error('Error loading templates from GitHub:', error);
        console.error('Error details:', {
            name: error.name,
            message: error.message,
            stack: error.stack
        });

        // Check if it's a network error
        if (error.message.includes('Failed to fetch') || error.name === 'TypeError') {
            alert('Network error: Please check your internet connection.');
        } else {
            alert(`Failed to load templates: ${error.message}`);
        }
    }
};

const showTemplateSelectionModal = (files, callbacks) => {
    // Create modal overlay
    const overlay = document.createElement('div');
    overlay.id = 'templateModalOverlay';
    overlay.style.cssText = `
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: rgba(0, 0, 0, 0.5);
        display: flex;
        justify-content: center;
        align-items: center;
        z-index: 10000;
    `;

    // Create modal content
    const modal = document.createElement('div');
    modal.style.cssText = `
        background-color: white;
        padding: 30px;
        border-radius: 8px;
        max-width: 500px;
        max-height: 70vh;
        overflow-y: auto;
        box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    `;

    const title = document.createElement('h2');
    title.textContent = 'Select a Template';
    title.style.cssText = 'margin-top: 0; margin-bottom: 20px;';

    const fileList = document.createElement('div');
    fileList.style.cssText = 'display: flex; flex-direction: column; gap: 10px;';

    // Create button for each file
    files.forEach(file => {
        const button = document.createElement('button');
        button.textContent = file.name;
        button.style.cssText = `
            padding: 12px 20px;
            background-color: #007bff;
            color: white;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            text-align: left;
            font-size: 14px;
            transition: background-color 0.2s;
        `;
        button.onmouseover = () => button.style.backgroundColor = '#0056b3';
        button.onmouseout = () => button.style.backgroundColor = '#007bff';

        button.onclick = async () => {
            overlay.remove();

            console.log('Selected file object:', file);
            console.log('download_url:', file.download_url);
            console.log('url:', file.url);

            // Use download_url from API response
            let downloadUrl = file.download_url;

            if (!downloadUrl) {
                console.warn('No download_url found, constructing raw URL manually');
                const pathPart = GITHUB_CONFIG.templatesPath ? `${GITHUB_CONFIG.templatesPath}/` : '';
                downloadUrl = `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${GITHUB_CONFIG.branch}/${pathPart}${encodeURIComponent(file.name)}`;
                console.log('Constructed URL:', downloadUrl);
            }

            await downloadAndLoadTemplate(downloadUrl, file.name, file.url, callbacks);
        };

        fileList.appendChild(button);
    });

    const cancelButton = document.createElement('button');
    cancelButton.textContent = 'Cancel';
    cancelButton.style.cssText = `
        margin-top: 20px;
        padding: 10px 20px;
        background-color: #6c757d;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        width: 100%;
    `;
    cancelButton.onclick = () => overlay.remove();

    modal.appendChild(title);
    modal.appendChild(fileList);
    modal.appendChild(cancelButton);
    overlay.appendChild(modal);
    document.body.appendChild(overlay);
};

const downloadAndLoadTemplate = async (downloadUrl, fileName, apiUrl, { setRows, setColumns, setListItems, onReceiveSavedStyles }) => {
    try {
        console.log('Downloading template from:', downloadUrl);
        console.log('File name:', fileName);
        console.log('API URL:', apiUrl);

        // Try the download URL first
        let response = await fetch(downloadUrl);

        // If 404, try alternate branch
        if (!response.ok && response.status === 404) {
            console.warn(`Download URL failed with 404, trying alternate branch`);

            const altBranch = GITHUB_CONFIG.branch === 'main' ? 'master' : 'main';
            const pathPart = GITHUB_CONFIG.templatesPath ? `${GITHUB_CONFIG.templatesPath}/` : '';
            const altDownloadUrl = `https://raw.githubusercontent.com/${GITHUB_CONFIG.owner}/${GITHUB_CONFIG.repo}/${altBranch}/${pathPart}${encodeURIComponent(fileName)}`;
            console.log('Trying alternate branch URL:', altDownloadUrl);
            response = await fetch(altDownloadUrl);

            if (!response.ok) {
                console.warn('Alternate branch failed, trying API endpoint with Accept header');

                const headers = {
                    'Accept': 'application/vnd.github.v3.raw'
                };

                response = await fetch(apiUrl, { headers });

                // If still fails, try getting base64 encoded content
                if (!response.ok) {
                    console.warn('Raw content failed, trying base64 encoded content');
                    const headersBase64 = {
                        'Accept': 'application/vnd.github.v3+json'
                    };

                    const base64Response = await fetch(apiUrl, { headers: headersBase64 });

                    if (!base64Response.ok) {
                        console.error('All download attempts failed:', base64Response.status, base64Response.statusText);
                        const errorText = await base64Response.text();
                        console.error('Error response:', errorText);
                        throw new Error(`Failed to download template: ${base64Response.status} - ${base64Response.statusText}`);
                    }

                    const fileData = await base64Response.json();
                    console.log('Got base64 encoded content');

                    // Decode base64 content
                    const jsonText = atob(fileData.content.replace(/\n/g, ''));
                    await processTemplateData(jsonText, fileName, { setRows, setColumns, setListItems, onReceiveSavedStyles });
                    return;
                }
            }
        }

        if (!response.ok) {
            console.error('Template download failed:', response.status, response.statusText);
            const errorText = await response.text();
            console.error('Error response:', errorText);
            throw new Error(`Failed to download template: ${response.status} - ${response.statusText}`);
        }

        const jsonText = await response.text();
        await processTemplateData(jsonText, fileName, { setRows, setColumns, setListItems, onReceiveSavedStyles });

    } catch (error) {
        console.error('Error downloading and loading template:', error);
        console.error('Error name:', error.name);
        console.error('Error message:', error.message);
        console.error('Error stack:', error.stack);
        alert(`Failed to load the selected template: ${error.message}`);
    }
};

const processTemplateData = async (jsonText, fileName, { setRows, setColumns, setListItems, onReceiveSavedStyles }) => {
    try {
        console.log('Template downloaded, parsing JSON...');

        const loadedData = JSON.parse(jsonText);
        console.log('JSON parsed successfully:', loadedData);

        // Process the loaded data (same logic as loadStateFromJSON)
        if (loadedData.rows && loadedData.columns && loadedData.tagLibrary) {
            const reconstructedRows = loadedData.rows.map(row => {
                const matchingSelections = loadedData.selectedTags?.find(sel => sel.rowId === row.id);
                if (matchingSelections) {
                    loadedData.columns.forEach(col => {
                        const key = `column${col.id}`;
                        row[`${key}_selected`] = matchingSelections.selections[key] || [];
                    });
                }
                return row;
            });

            setRows(reconstructedRows);
            setColumns(loadedData.columns);
            setListItems(loadedData.tagLibrary);
        } else {
            console.error('Invalid JSON structure');
            alert('Invalid template file structure.');
            return;
        }

        if (loadedData.savedStyles && typeof onReceiveSavedStyles === 'function') {
            onReceiveSavedStyles(loadedData.savedStyles);
        }

        if (Array.isArray(loadedData.favSavedStyles)) {
            console.log("FunctionsLoadTemplate.jsx read favSavedStyles", loadedData.favSavedStyles);

            const loadFavSavedStyles = (favStylesArray) => {
                setTimeout(() => {
                    favStylesArray.forEach((styleId) => {
                        const match = styleId.match(/^favButtonGroupBox-(\d+)$/);
                        if (match) {
                            const index = match[1];
                            const container = document.getElementById('toolSavedStylesContainerPosition');
                            if (container) {
                                const buttonId = `buttonAddToFavoriteSavedStyles-${index}`;
                                const button = container.querySelector(`button#${buttonId}`);
                                if (button) {
                                    console.log(`${buttonId} FOUND`);
                                    button.click();
                                } else {
                                    console.log(`${buttonId} NOT FOUND`);
                                }
                            }
                        }
                    });
                }, 2000);
            };

            loadFavSavedStyles(loadedData.favSavedStyles);
        }

        // Strip .onewriter or .json extension
        const rawFileName = fileName.replace(/\.(onewriter|json)$/i, '');
        const fileNameInput = document.getElementById('fileNameInput');
        if (fileNameInput) fileNameInput.value = rawFileName;

        console.log(`Loaded template file: "${rawFileName}"`);

        // Scroll up to top
        clickScrollUpToFirstAfterLoad();

    } catch (error) {
        console.error('Error processing template data:', error);
        throw error;
    }
};

const clickScrollUpToFirstAfterLoad = () => {
    setTimeout(() => {
        const scrollUpButton = document.getElementById('buttonScrollUp');
        if (scrollUpButton) {
            console.log('clickScrollUpToFirstAfterLoad: buttonScrollUp found, clicking...');
            scrollUpButton.click();
        } else {
            console.warn('clickScrollUpToFirstAfterLoad: buttonScrollUp not found in DOM.');
        }
    }, 1000);
};

const FunctionsLoadTemplate = () => null;
export default FunctionsLoadTemplate;